/**
 * Free Shipping Welcome Feature
 * Auto-applies the FREESHIPNEW discount for new customers.
 *
 * Eligibility: no orders on the account. Nothing else — not the traffic source, not the
 * landing page. It used to be Meta paid traffic only, which left an organic new customer
 * being counted up to the £40 threshold while a paid one shipped free on the same basket.
 *
 * A logged-out repeat buyer is invisible to the theme, so the copy this drives names the
 * audience ("new customer", "first order") rather than promising everyone free shipping,
 * and Shopify's own refusal is honoured: a code that comes back inapplicable flips the
 * state to USED and every surface stops offering it.
 *
 * Exposes window.FreeShipWelcome API:
 * - getState() - 'eligible', 'applied', 'used', 'not_eligible'
 * - isEligible() - true if eligible or applied
 * - isApplied() - true if discount is active in cart
 * - isUsed() - true if code was already used by customer
 * - getCustomerStatus() - { isLoggedIn, hasOrders }
 * - getDiscountCode() - returns the discount code string
 *
 * Events dispatched:
 * - freeship-welcome:ready - when initialization complete
 * - freeship-welcome:updated - when state changes
 */

(function() {
  'use strict';

  // Configuration - can be overridden via window.FreeShipWelcomeConfig
  const CONFIG = Object.assign({
    DISCOUNT_CODE: 'FREESHIPNEW',
    STORAGE_KEY: 'pemmican_free_ship_welcome',
    DEBUG: false
  }, window.FreeShipWelcomeConfig || {});

  // Applying the code is a redirect-shaped request, and a free-shipping discount leaves
  // no line to check afterwards, so remember the attempt for the tab rather than repeat
  // it on every page view.
  const SESSION_APPLIED_KEY = 'pemmican_free_ship_applied';

  // State constants
  const STATE = {
    ELIGIBLE: 'eligible',           // New customer, discount not confirmed in cart yet
    APPLIED: 'applied',             // Discount is in the cart
    USED: 'used',                   // Shopify refused it, or the account has orders
    NOT_ELIGIBLE: 'not_eligible'    // Feature off / nothing known yet
  };

  /**
   * Debug logger
   */
  function log(...args) {
    if (CONFIG.DEBUG) {
      console.log('[FreeShipWelcome]', ...args);
    }
  }

  /**
   * Get stored state from localStorage
   */
  function getStoredState() {
    try {
      const stored = localStorage.getItem(CONFIG.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      log('Storage read error:', e);
    }
    return null;
  }

  /**
   * Store state in localStorage
   */
  function storeState(state, extra = {}) {
    try {
      localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify({
        state: state,
        timestamp: Date.now(),
        ...extra
      }));
      log('State stored:', state);
    } catch (e) {
      log('Storage write error:', e);
    }
  }

  /**
   * Apply the discount. Shopify's /discount/<code> route sets it on the session cart.
   */
  async function applyDiscountViaFetch() {
    try {
      log('Applying discount via fetch');
      const response = await fetch(`/discount/${CONFIG.DISCOUNT_CODE}`, {
        method: 'GET',
        credentials: 'same-origin'
      });
      return response.ok;
    } catch (e) {
      log('Fetch discount error:', e);
      return false;
    }
  }

  /**
   * Check cart for discount application status
   */
  async function checkDiscountStatus() {
    try {
      const response = await fetch('/cart.js', {
        credentials: 'same-origin'
      });
      const cart = await response.json();

      // discount_codes is the one place a free-shipping code shows up: it has no cart
      // line to allocate against, so it never reaches discount_applications at all —
      // established in the cart2 work, 7e0e8fa. Each entry carries Shopify's verdict in
      // `applicable`, which is how a logged-out repeat buyer gets found out.
      const entry = (Array.isArray(cart.discount_codes) ? cart.discount_codes : []).find(
        (d) => String(d.code || '').toUpperCase() === CONFIG.DISCOUNT_CODE
      );

      // Applications are still worth reading: a code the shop rewrites into a different
      // discount type would land there instead.
      const inApplications = cart.discount_applications?.some(d => {
        const title = d.title?.toUpperCase();
        const code = d.code?.toUpperCase();
        return title === CONFIG.DISCOUNT_CODE || code === CONFIG.DISCOUNT_CODE;
      });

      log('Discount status check:', entry, inApplications);

      return {
        isApplied: !!inApplications || !!(entry && entry.applicable !== false),
        isRefused: !!(entry && entry.applicable === false),
        cart: cart
      };
    } catch (e) {
      log('Cart fetch error:', e);
      return { isApplied: false, isRefused: false, cart: null };
    }
  }

  /**
   * Get current customer status
   * Relies on window.pemmicanCustomer set by Liquid in theme.liquid
   */
  function getCustomerStatus() {
    return {
      isLoggedIn: window.pemmicanCustomer?.isLoggedIn || false,
      hasOrders: window.pemmicanCustomer?.hasOrders || false
    };
  }

  /**
   * Create the public API object
   */
  function createAPI(state) {
    return {
      getState: () => state,
      isEligible: () => [STATE.ELIGIBLE, STATE.APPLIED].includes(state),
      isApplied: () => state === STATE.APPLIED,
      isUsed: () => state === STATE.USED,
      getDiscountCode: () => CONFIG.DISCOUNT_CODE,
      getCustomerStatus: getCustomerStatus,
      STATE: STATE,
      // For debugging
      _refresh: init,
      _config: CONFIG
    };
  }

  /**
   * Dispatch state event
   */
  function dispatchEvent(eventName, state) {
    const api = createAPI(state);
    window.FreeShipWelcome = api;

    document.dispatchEvent(new CustomEvent(eventName, {
      detail: api
    }));

    log('Event dispatched:', eventName, state);
  }

  /**
   * Main initialization
   */
  /**
   * Whether to ask Shopify for the code, given what the cart currently holds.
   */
  function shouldApply(status) {
    if (!status.cart || status.isApplied || status.isRefused) return false;

    // Another code owns the cart's discount slot. Sending ours alone REPLACES the set
    // rather than adding to it — verified live on this shop, 7e0e8fa — so a trial-pack
    // buyer would lose POPUPCRISP to gain what assets/trial-pack-offer.js is already
    // applying for them, in one request, with both codes in it.
    const codes = Array.isArray(status.cart.discount_codes) ? status.cart.discount_codes : [];
    if (codes.length > 0) return false;

    return true;
  }

  /**
   * At most one request per cart shape per tab: once while the cart is empty, once after
   * it has something in it. The second one is the one that matters — an empty cart holds
   * no discount codes, so an early attempt cannot be verified and may not survive the
   * first add — and it is why the code is attached by the time the cart page is reached.
   */
  function applyBucket(cart) {
    return cart && cart.item_count > 0 ? 'items' : 'empty';
  }

  function alreadyTried(bucket) {
    try {
      return sessionStorage.getItem(SESSION_APPLIED_KEY) === bucket;
    } catch (e) {
      return false;
    }
  }

  function recordTry(bucket) {
    try { sessionStorage.setItem(SESSION_APPLIED_KEY, bucket); } catch (e) {}
  }

  /**
   * Turn a cart verdict into a state, and tell everyone.
   */
  function settle(status, eventName) {
    let state = STATE.ELIGIBLE;
    if (status.isRefused) {
      state = STATE.USED;
    } else if (status.isApplied) {
      state = STATE.APPLIED;
    }
    storeState(state);
    dispatchEvent(eventName, state);
    return state;
  }

  /**
   * Ask for the code if the cart needs it, then report what the cart says.
   */
  async function ensureApplied(status, eventName) {
    const bucket = applyBucket(status.cart);

    if (shouldApply(status) && !alreadyTried(bucket)) {
      log('Applying discount, cart is', bucket);
      recordTry(bucket);
      await applyDiscountViaFetch();
      // Shopify needs a moment before /cart.js reflects it.
      await new Promise((resolve) => setTimeout(resolve, 300));
      return settle(await checkDiscountStatus(), eventName);
    }

    return settle(status, eventName);
  }

  async function init() {
    log('Initializing...');

    // One rule: a new customer gets the welcome, whatever brought them here. Orders on
    // the account are the only disqualifier the theme can see; a logged-out repeat buyer
    // is caught by Shopify refusing the code, which is read back below.
    if (getCustomerStatus().hasOrders) {
      log('Account has orders, not eligible');
      dispatchEvent('freeship-welcome:ready', STATE.USED);
      return;
    }

    const stored = getStoredState();
    if (stored?.state === STATE.USED) {
      log('Previously refused');
      dispatchEvent('freeship-welcome:ready', STATE.USED);
      return;
    }

    await ensureApplied(await checkDiscountStatus(), 'freeship-welcome:ready');
  }

  /**
   * Handle cart updates - re-check discount status
   */
  async function onCartUpdate() {
    if (getCustomerStatus().hasOrders) return;
    const stored = getStoredState();
    if (stored?.state === STATE.USED) return;

    // This is where the cart first has something to judge. Shopify only rules on a code
    // once there are lines to apply it against, so both the refusal and the confirmation
    // usually arrive here rather than at load — and a cart that gained its first item
    // gets the request it could not usefully have had while it was empty.
    const state = await ensureApplied(await checkDiscountStatus(), 'freeship-welcome:updated');
    log('Cart update settled as', state);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Subscribe to cart updates
  function setupCartListener() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
      log('Cart update listener attached');
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartListener);
  } else {
    setupCartListener();
  }

  // Expose placeholder API immediately (will be updated after init).
  // init() can reach a verdict on its synchronous path — a returning customer does —
  // and that happens before this line runs, so don't stamp the placeholder over it.
  if (!window.FreeShipWelcome) {
    window.FreeShipWelcome = createAPI(STATE.NOT_ELIGIBLE);
  }
})();
